import { Tabs, TabsContent, TabsList, TabsTrigger } from '@xhubio-saas/core-frontend/ui-components'
import { type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'

/** Die drei Ansichten der Werkbank: was hinein ging, was herauskam, wie der Aufruf aussah. */
export type WorkbenchTab = 'file' | 'parsed' | 'request'

/** Der Schluessel, unter dem die Werkbank ihren gewaehlten Tab fuer die Sitzung merkt. */
export const WORKBENCH_TAB_KEY = 'invoice-validation:tab'

/**
 * Der gemerkte Tab. 🔴 `sessionStorage` kann fehlen oder werfen (privates Fenster, gesperrte Website-Daten) — dann
 * gilt der erste vorhandene Tab, die Seite funktioniert ohne Speicher.
 */
function readTab(key: string): string | null {
  try {
    return sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function writeTab(key: string, tab: string): void {
  try {
    sessionStorage.setItem(key, tab)
  } catch {
    // Ohne Speicher gilt die Wahl nur bis zum Neuladen.
  }
}

/** Feste Klassen je Tab-Zahl — Tailwind sieht nur ausgeschriebene Klassen. */
const GRID_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
}

export interface WorkbenchPanel {
  /** Die Validierung nutzt `WorkbenchTab`; andere Seiten (Konvertierung) eigene Kennungen. */
  id: WorkbenchTab | (string & {})
  label: string
  /** Kurze Formatangabe hinter dem Label (`XML`, `JSON`) — ab `sm` sichtbar, darunter traegt sie der Inhalt. */
  hint?: string
  content: ReactNode
}

/**
 * Die Werkbank — Rohdaten der Pruefung in EINEM Bereich mit Tabs statt drei gestapelter Bloecke.
 *
 * 🔴 Tabs nach `.claude/UI_GUIDELINES.md` §27 (Segmented Control, `grid w-full grid-cols-n`). Nur Tabs, deren Inhalt
 * es gibt: ohne geparste Rechnung kein „Geparst", ohne Anfrage keine „Anfrage".
 *
 * ⚪ `keepMounted`: die verborgenen Tabs bleiben im DOM (`hidden`). Der Zustand des CodeViewer (gefaltete Abschnitte,
 * Vollbild) und des Inspectors ueberlebt so den Tab-Wechsel.
 */
export interface WorkbenchProps {
  panels: WorkbenchPanel[]
  /** Schluessel fuer den gemerkten Tab — Standard `WORKBENCH_TAB_KEY` (Validierung). Zwei Werkbaenke einer Seite brauchen zwei. */
  storageKey?: string
  /** Praefix der `data-testid` — Standard `invoice-validation`. */
  testIdPrefix?: string
  /** Name des Bereichs fuer Screenreader — Standard „Werkbank" (`invoiceValidation.werkbank.title`). */
  label?: string
  /**
   * Knoepfe rechts neben der Tab-Leiste (Konvertierung: Herunterladen). Ohne `actions` steht die Leiste allein wie
   * bisher; mit ihnen bricht die Zeile schmal um — die Tabs behalten die ganze Breite, die Knoepfe rutschen darunter.
   */
  actions?: ReactNode
}

export function Workbench({
  panels,
  storageKey = WORKBENCH_TAB_KEY,
  testIdPrefix = 'invoice-validation',
  label,
  actions,
}: WorkbenchProps) {
  const { t } = useTranslation()
  const [stored, setStored] = useState<string | null>(() => readTab(storageKey))
  const first = panels[0]
  if (!first) return null
  const active = panels.some((p) => p.id === stored) ? (stored as string) : first.id

  const list = (
    <TabsList className={`grid w-full ${GRID_COLS[panels.length] ?? 'grid-cols-3'}`}>
      {panels.map((p) => (
        <TabsTrigger
          key={p.id}
          value={p.id}
          // 🔴 Eigener Fokusring: der Ring des Primitives (`focus-visible:ring-[3px]`) kam im Browser nicht an
          // (gemessen 2026-10-10: box-shadow transparent, outline none). Outline statt Schatten — keine
          // Verschiebung, nicht animiert. Der Uebergang gilt NUR Hintergrund und Text (`transition-colors` schloesse
          // `outline-color` ein, der Ring blendete 150 ms ein — Hallmark-Gate 15) und ersetzt das `transition-all`
          // des Primitives (Gate 10). Gedrueckt (Gate 26): leicht abgedunkelt, nur Tokens, kein transform.
          className="min-w-0 transition-[background-color,color] motion-reduce:transition-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring active:bg-muted data-active:active:bg-primary/90"
          data-testid={`${testIdPrefix}-tab-${p.id}`}
        >
          <span className="truncate">{p.label}</span>
          {p.hint && (
            // Ohne `opacity-70`: gemessen 2026-10-10 lag der Hinweis damit bei 2.74:1 (inaktiv) bzw. 3.92:1 (aktiv) —
            // unter 4.5:1. Abgesetzt nur durch Groesse und Gewicht, die Farbe erbt er vom Tab.
            <span className="hidden text-xs font-normal sm:inline">{p.hint}</span>
          )}
        </TabsTrigger>
      ))}
    </TabsList>
  )

  return (
    <section
      aria-label={label ?? t('invoiceValidation.werkbank.title', 'Werkbank')}
      data-testid={`${testIdPrefix}-workbench`}
    >
      <Tabs
        value={active}
        onValueChange={(value) => {
          const tab = String(value)
          setStored(tab)
          writeTab(storageKey, tab)
        }}
      >
        {actions ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <div className="min-w-0 flex-[1_1_16rem]">{list}</div>
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          </div>
        ) : (
          list
        )}
        {panels.map((p) => (
          <TabsContent
            key={p.id}
            value={p.id}
            keepMounted
            className="min-w-0 pt-2"
            data-testid={`${testIdPrefix}-panel-${p.id}`}
          >
            {p.content}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  )
}
